import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Disclosure } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/outline';

interface Step {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
}

interface ImplementationStepsProps {
  steps: Step[];
  timeline: string;
  pricing: {
    upfront: string;
    monthly?: string;
  };
}

const ImplementationSteps: React.FC<ImplementationStepsProps> = ({ 
  steps, 
  timeline, 
  pricing 
}) => {
  const [openStep, setOpenStep] = useState<number | null>(null);

  const toggleStep = (stepId: number) => {
    setOpenStep(openStep === stepId ? null : stepId);
  };

  return (
    <div className="bg-card dark:bg-dark-card rounded-lg shadow-soft dark:shadow-dark-card">
      <h3 className="text-lg font-semibold text-card-foreground dark:text-dark-accent p-6 border-b border-border dark:border-dark-border">
        Implementation Made Easy
      </h3>
      
      <div className="divide-y divide-border dark:divide-dark-border">
        {steps.map((step) => (
          <Disclosure key={step.id} as="div">
            {({ open }) => (
              <>
                <Disclosure.Button
                  className="w-full px-6 py-4 flex items-center justify-between text-left focus:outline-none focus:ring-2 focus:ring-ring dark:focus:ring-dark-ring focus:ring-offset-2 dark:focus:ring-offset-dark-card"
                  onClick={() => toggleStep(step.id)}
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary/10 dark:bg-dark-primary/10 flex items-center justify-center text-primary dark:text-dark-primary-foreground">
                      {step.icon}
                    </div>
                    <div className="ml-4">
                      <span className="text-sm font-medium text-card-foreground dark:text-dark-card-foreground">Step {step.id}</span>
                      <h4 className="text-base font-semibold text-primary dark:text-dark-accent">{step.title}</h4>
                    </div>
                  </div>
                  <ChevronDownIcon
                    className={`${
                      open ? 'transform rotate-180' : ''
                    } w-5 h-5 text-primary dark:text-dark-accent transition-transform duration-200`}
                  />
                </Disclosure.Button>
                
                <AnimatePresence>
                  {open && (
                    <Disclosure.Panel static as={motion.div}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 py-4 bg-secondary dark:bg-dark-secondary">
                        <p className="text-sm text-secondary-foreground dark:text-dark-secondary-foreground">{step.description}</p>
                      </div>
                    </Disclosure.Panel>
                  )}
                </AnimatePresence>
              </>
            )}
          </Disclosure>
        ))}
      </div>
      
      <div className="px-6 py-4 bg-secondary dark:bg-dark-secondary rounded-b-lg">
        <div className="flex flex-col sm:flex-row sm:justify-between">
          <div className="mb-2 sm:mb-0">
            <span className="text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">Timeline:</span>
            <span className="ml-2 text-sm font-semibold text-secondary-foreground dark:text-dark-secondary-foreground">{timeline}</span>
          </div>
          <div>
            <span className="text-sm font-medium text-muted-foreground dark:text-dark-muted-foreground">Pricing:</span>
            <span className="ml-2 text-sm font-semibold text-accent dark:text-dark-accent">
              {pricing.upfront} {pricing.monthly && `+ ${pricing.monthly}/month`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImplementationSteps;
